import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { SessionUser } from '@toga/shared'
import { api, ApiError, resetCsrfToken, setUnauthorizedHandler } from '../lib/api'

type AuthState =
  | { status: 'loading'; user: null }
  | { status: 'unauthenticated'; user: null }
  | { status: 'authenticated'; user: SessionUser }

export type LoginResult =
  | { kind: 'ok'; user: SessionUser }
  /** El usuario tiene MFA activo y debe reintentar con código. */
  | { kind: 'mfa-required' }
  /**
   * El usuario debe configurar MFA antes de poder usar el sistema.
   * La sesión está creada y el user disponible, pero la UI debe
   * forzar la navegación a /settings/security.
   */
  | { kind: 'enrollment-required'; user: SessionUser }

type AuthActions = {
  login: (
    email: string,
    password: string,
    opts?: { mfaCode?: string; recoveryCode?: string },
  ) => Promise<LoginResult>
  logout: () => Promise<void>
  changePassword: (
    currentPassword: string,
    newPassword: string,
    confirmPassword: string,
  ) => Promise<void>
  refresh: () => Promise<void>
}

type AuthContextValue = AuthState &
  AuthActions & {
    /** true si la sesión murió en el servidor (timeout/revocación) con la app
     * abierta. El login lo usa para mostrar el aviso "sesión caducada". */
    sessionExpired: boolean
  }

const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>')
  return ctx
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null })
  const [sessionExpired, setSessionExpired] = useState(false)

  // Espejo del status para que el handler global de 401 (registrado una sola
  // vez) lea el valor actual sin re-registrarse en cada render.
  const statusRef = useRef<AuthState['status']>('loading')
  statusRef.current = state.status

  const refresh = useCallback(async () => {
    try {
      const { user } = await api.get<{ user: SessionUser }>('/auth/me')
      setSessionExpired(false)
      setState({ status: 'authenticated', user })
    } catch {
      setState({ status: 'unauthenticated', user: null })
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Cualquier 401 de la API con la app ya autenticada = sesión caducada o
  // revocada en el servidor → pasamos a 'unauthenticated' y AuthGuard
  // redirige a /login conservando la ruta de origen.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (statusRef.current !== 'authenticated') return
      setSessionExpired(true)
      setState({ status: 'unauthenticated', user: null })
    })
    return () => setUnauthorizedHandler(null)
  }, [])

  const login = useCallback<AuthActions['login']>(async (email, password, opts) => {
    try {
      const resp = await api.post<
        | { user: SessionUser }
        | { mfaRequired: true }
        | { mfaEnrollmentRequired: true }
      >('/auth/login', {
        email,
        password,
        ...(opts?.mfaCode ? { mfaCode: opts.mfaCode } : {}),
        ...(opts?.recoveryCode ? { recoveryCode: opts.recoveryCode } : {}),
      })
      resetCsrfToken()
      if ('user' in resp) {
        setSessionExpired(false)
        setState({ status: 'authenticated', user: resp.user })
        return { kind: 'ok', user: resp.user }
      }
      if ('mfaEnrollmentRequired' in resp) {
        // La sesión está creada en el servidor; recuperamos el user.
        await refresh()
        // Después del refresh el state debería ser 'authenticated'.
        // Sacamos el user del estado actualizado leyéndolo del /auth/me directo
        // para asegurar la consistencia.
        const { user } = await api.get<{ user: SessionUser }>('/auth/me')
        setState({ status: 'authenticated', user })
        return { kind: 'enrollment-required', user }
      }
      // mfaRequired
      return { kind: 'mfa-required' }
    } catch (err) {
      // El backend devuelve 401 con { mfaRequired: true } si falta el segundo factor.
      if (err instanceof ApiError && err.status === 401) {
        const body = err.body as { mfaRequired?: boolean } | undefined
        if (body?.mfaRequired) return { kind: 'mfa-required' }
      }
      throw err
    }
  }, [refresh])

  const logout = useCallback<AuthActions['logout']>(async () => {
    try {
      await api.post('/auth/logout')
    } finally {
      resetCsrfToken()
      setSessionExpired(false)
      setState({ status: 'unauthenticated', user: null })
    }
  }, [])

  const changePassword = useCallback<AuthActions['changePassword']>(
    async (currentPassword, newPassword, confirmPassword) => {
      await api.post('/auth/change-password', { currentPassword, newPassword, confirmPassword })
      await refresh()
    },
    [refresh],
  )

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, sessionExpired, login, logout, changePassword, refresh }),
    [state, sessionExpired, login, logout, changePassword, refresh],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
